import {
  EducationDegree,
  EducationFaculty,
  EducationInstitution,
} from "../models/education.model";
import { BackendHttpService } from "./backendHttpService";

// Interface for Education data that matches backend PersonEducationModel
export interface PersonEducationModel {
  id: number;
  userId: number;
  dateStart: string; // ISO date string
  dateEnd?: string; // ISO date string or null
  degree?: {
    id: number;
    name: string;
  };
  institution: {
    id: number;
    name: string;
  };
  faculty?: {
    id: number;
    name: string;
  };
}

// Legacy interface for backward compatibility
interface UserEducationItem {
  id: number;
  userId: number;
  institutionId: number;
  degreeId: number;
  facultyId: number;
  dateStart: Date;
  dateEnd: Date | null;
  institution?: {
    id: number;
    name: string;
  };
  degree?: {
    id: number;
    name: string;
  };
  faculty?: {
    id: number;
    name: string;
  };
}

export class EducationService extends BackendHttpService {
  // Cache for each dictionary (read-only access for users)
  private degreesDictionaryCache: EducationDegree[] | null = null;
  private institutionsDictionaryCache: EducationInstitution[] | null = null;
  private facultiesDictionaryCache: EducationFaculty[] | null = null;

  constructor() {
    super("Education");
  }

  // Clear specific cache when needed
  private clearDegreesCache(): void {
    this.degreesDictionaryCache = null;
  }

  private clearInstitutionsCache(): void {
    this.institutionsDictionaryCache = null;
  }

  private clearFacultiesCache(): void {
    this.facultiesDictionaryCache = null;
  }

  // Education Degrees (Read-only for user forms/dropdowns)
  async getEducationDegrees(): Promise<EducationDegree[]> {
    if (this.degreesDictionaryCache) {
      return this.degreesDictionaryCache;
    }
    try {
      this.degreesDictionaryCache = await this.get<EducationDegree[]>(
        "/degreesDictionary"
      );
      return this.degreesDictionaryCache;
    } catch (error) {
      console.error("Error getting education degrees:", error);
      throw error;
    }
  }

  // Education Institutions (Read-only for user forms/dropdowns)
  async getEducationInstitutions(): Promise<EducationInstitution[]> {
    if (this.institutionsDictionaryCache) {
      return this.institutionsDictionaryCache;
    }
    try {
      this.institutionsDictionaryCache = await this.get<EducationInstitution[]>(
        "/institutionsDictionary"
      );
      return this.institutionsDictionaryCache;
    } catch (error) {
      console.error("Error getting education institutions:", error);
      throw error;
    }
  }

  // Education Faculties (Read-only for user forms/dropdowns)
  async getEducationFaculties(): Promise<EducationFaculty[]> {
    if (this.facultiesDictionaryCache) {
      return this.facultiesDictionaryCache;
    }
    try {
      this.facultiesDictionaryCache = await this.get<EducationFaculty[]>(
        "/facultiesDictionary"
      );
      return this.facultiesDictionaryCache;
    } catch (error) {
      console.error("Error getting education faculties:", error);
      throw error;
    }
  }

  // USER EDUCATION METHODS (User data CRUD operations)

  // Get education list for a specific user
  async getEducationListByPersonId(personId: number): Promise<PersonEducationModel[]> {
    try {
      return await this.get<PersonEducationModel[]>(`/byUserId/${personId}`);
    } catch (error) {
      console.error("Error getting education list for person:", error);
      throw error;
    }
  }

  // Alternative method name used in some components
  async getDataByPersonID(personId: number): Promise<PersonEducationModel[]> {
    return this.getEducationListByPersonId(personId);
  }

  // Get a specific education item by ID
  async getEducationById(educationId: number): Promise<PersonEducationModel> {
    try {
      return await this.get<PersonEducationModel>(`/${educationId}`);
    } catch (error) {
      console.error("Error getting education by ID:", error);
      throw error;
    }
  }

  // Add a new education item for a user
  async addEducationItem(
    educationModel: PersonEducationModel
  ): Promise<PersonEducationModel> {
    try {
      return await this.post<PersonEducationModel, PersonEducationModel>(
        educationModel,
        ""
      );
    } catch (error) {
      console.error("Error adding education item:", error);
      throw error;
    }
  }

  // Update an existing education item
  async updateEducationItem(
    educationModel: PersonEducationModel
  ): Promise<PersonEducationModel> {
    try {
      return await this.put<PersonEducationModel, PersonEducationModel>(
        educationModel,
        ""
      );
    } catch (error) {
      console.error("Error updating education item:", error);
      throw error;
    }
  }

  // Delete an education item by ID
  async deleteEducationItem(educationId: number): Promise<void> {
    try {
      await this.delete(`/${educationId}`);
    } catch (error) {
      console.error("Error deleting education item:", error);
      throw error;
    }
  }
}
